/**
 * `/admin` — accounts, and how people get them.
 *
 * The rails here are the server's, restated in the UI only so a button that
 * would fail is not offered: the last-active-admin rule, self-demotion and
 * self-deletion are all refused server-side whatever this screen renders.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'wouter'
import type { AdminUser, Settings, Invite, Role } from '@server/auth/store.ts'
import { ApiError, api } from '~/lib/api.ts'
import { useAuth } from '~/lib/auth.tsx'
import { useT } from '~/i18n/index.tsx'
import { count, timestamp } from '~/i18n/format.ts'
import { Badge, Button, Label, Skeleton } from '~/components/ui/primitives.tsx'
import { EmptyState, ErrorState, Command } from '~/components/ui/empty.tsx'
import { Table, Td, Th, Tr } from '~/components/ui/table.tsx'
import { Dialog } from '~/components/ui/overlay.tsx'
import { Segmented } from '~/components/ui/tabs.tsx'
import { TextInput, Message } from './login.tsx'
import { cn } from '~/lib/cn.ts'
import { useDocumentTitle } from '~/lib/title.ts'

type UsersResponse = { users: AdminUser[]; settings: Settings; passwordMin: number }

export function AdminScreen() {
  const { t, locale } = useT()
  useDocumentTitle(t('admin.title'))
  const { user: me } = useAuth()
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [resetting, setResetting] = useState<AdminUser | null>(null)

  const { data, error, refetch } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api<UsersResponse>('/api/admin/users'),
  })

  const patch = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) =>
      api(`/api/admin/users/${v.id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(v.body),
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/admin/users/${id}`, { method: 'DELETE' }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
  const signOutAll = useMutation({
    mutationFn: (id: string) => api(`/api/admin/users/${id}/signout`, { method: 'POST' }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
  const setRegistration = useMutation({
    mutationFn: (registration: string) =>
      api('/api/admin/settings', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ registration }),
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })

  if (error) {
    const e = error as ApiError
    return (
      <Column>
        {e.status === 403
          ? <EmptyState title={t('empty.generic')}>
              <Link href="/results" className="link-inline text-[var(--accent)]">{t('nav.results')}</Link>
            </EmptyState>
          : <ErrorState title={t('common.error')} detail={e.message}
              onRetry={() => void refetch()} retryLabel={t('action.retry')} />}
      </Column>
    )
  }
  if (!data) return <Column><Skeleton className="h-96 w-full" /></Column>

  const n = (x: number) => count(x, locale)
  const err = (patch.error ?? remove.error) as ApiError | null

  return (
    <Column>
      <header className="pb-3">
        <h1 className="text-[length:var(--text-display)] font-semibold tracking-tight">{t('admin.title')}</h1>
        <p className="mt-1 text-[length:var(--text-data)] text-[var(--text-muted)]">{t('admin.subtitle')}</p>
      </header>

      {err && <div className="mb-3"><Message tone="fail">{err.message}</Message></div>}

      <div className="mb-3 flex flex-wrap items-end gap-x-4 gap-y-2">
        <div>
          <Label>{t('admin.registration')}</Label>
          <div className="mt-1">
            <Segmented
              size="sm"
              ariaLabel={t('admin.registration')}
              value={data.settings.registration}
              onChange={(v) => setRegistration.mutate(v)}
              options={[
                { id: 'closed', label: t('admin.registration.closed') },
                { id: 'invite', label: t('admin.registration.invite') },
                { id: 'request', label: t('admin.registration.request') },
              ]}
            />
          </div>
        </div>
        <div className="flex-1" />
        <Button variant="primary" onClick={() => setAdding(true)}>{t('admin.newUser')}</Button>
      </div>

      <Table>
        <thead>
          <Tr>
            <Th>{t('admin.username')}</Th>
            <Th>{t('admin.role')}</Th>
            <Th>{t('admin.status')}</Th>
            <Th numeric>{t('admin.sessions')}</Th>
            <Th>{t('admin.lastLogin')}</Th>
            <Th>{t('admin.actions')}</Th>
          </Tr>
        </thead>
        <tbody>
          {data.users.map((u) => {
            const self = u.id === me?.id
            return (
              <Tr key={u.id}>
                <Td>
                  <span className="font-medium text-[var(--text)]">{u.username}</span>
                  {self && <span className="ms-1.5 text-[length:var(--text-micro)] text-[var(--text-faint)]">({t('admin.you')})</span>}
                  {u.displayName && (
                    <div className="text-[length:var(--text-micro)] text-[var(--text-muted)]">{u.displayName}</div>
                  )}
                </Td>
                <Td>
                  <Segmented
                    size="sm"
                    ariaLabel={t('admin.role')}
                    value={u.role}
                    onChange={(role) => patch.mutate({ id: u.id, body: { role } })}
                    options={[
                      { id: 'viewer', label: t('admin.role.viewer') },
                      { id: 'admin', label: t('admin.role.admin') },
                    ]}
                  />
                </Td>
                <Td>
                  {u.disabled
                    ? <Badge state="warn">{u.lastLoginAt === null ? t('admin.pending') : t('admin.disabled')}</Badge>
                    : <Badge state="pass">{t('admin.active')}</Badge>}
                </Td>
                <Td numeric className="tnum text-[var(--text-muted)]">{n(u.sessions)}</Td>
                <Td className="text-[var(--text-muted)]">
                  {u.lastLoginAt ? timestamp(new Date(u.lastLoginAt).toISOString(), locale)
                    : <span className="text-[var(--text-faint)]">{t('admin.never')}</span>}
                </Td>
                <Td>
                  <div className="flex flex-wrap gap-1">
                    <Button size="sm" variant="ghost" disabled={self}
                      onClick={() => patch.mutate({ id: u.id, body: { disabled: !u.disabled } })}>
                      {u.disabled ? (u.lastLoginAt === null ? t('admin.approve') : t('admin.enable')) : t('admin.disable')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setResetting(u)}>
                      {t('admin.resetPassword')}
                    </Button>
                    <Button size="sm" variant="ghost" disabled={u.sessions === 0}
                      onClick={() => signOutAll.mutate(u.id)}>
                      {t('admin.signOutAll')}
                    </Button>
                    <Button size="sm" variant="danger" disabled={self}
                      onClick={() => {
                        if (confirm(t('admin.deleteConfirm', { name: u.username }))) remove.mutate(u.id)
                      }}>
                      {t('admin.delete')}
                    </Button>
                  </div>
                </Td>
              </Tr>
            )
          })}
        </tbody>
      </Table>

      <p className="mt-2 max-w-[76ch] text-[length:var(--text-micro)] text-[var(--text-faint)]">
        {t('admin.roleWhy')}
      </p>

      {data.settings.registration === 'invite' && <Invites />}

      <AddUser open={adding} onClose={() => setAdding(false)} min={data.passwordMin} />
      <ResetPassword user={resetting} onClose={() => setResetting(null)} min={data.passwordMin} />
    </Column>
  )
}

// ---------------------------------------------------------------------

function AddUser({ open, onClose, min }: { open: boolean; onClose: () => void; min: number }) {
  const { t } = useT()
  const qc = useQueryClient()
  const [role, setRole] = useState<Role>('viewer')
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<{ username: string; password: string } | null>(null)

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api('/api/admin/users', {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
      }),
    onSuccess: (_r, body) => {
      setCreated({ username: String(body.username), password: String(body.password) })
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
    onError: (e) => setError((e as ApiError).message),
  })

  const close = () => { setCreated(null); setError(null); onClose() }

  return (
    <Dialog open={open} onClose={close} title={t('admin.newUser')} closeLabel={t('action.close')} width={460}>
      {created ? (
        <div className="space-y-2">
          <Message tone="pass">{t('admin.created1')}</Message>
          <div>
            <Label>{t('admin.username')}</Label>
            <Command>{created.username}</Command>
          </div>
          <div>
            <Label>{t('admin.tempPassword')}</Label>
            <Command>{created.password}</Command>
          </div>
          <Button onClick={close} className="w-full">{t('action.close')}</Button>
        </div>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault(); setError(null)
            const f = new FormData(e.currentTarget)
            create.mutate({
              username: String(f.get('username') ?? '').trim().toLowerCase(),
              password: String(f.get('password') ?? ''),
              displayName: String(f.get('displayName') ?? '').trim() || undefined,
              role,
              mustChangePassword: f.get('mustChange') === 'on',
            })
          }}
        >
          <TextInput name="username" label={t('admin.username')} autoFocus required />
          <TextInput name="displayName" label={t('admin.name')} />
          <TextInput name="password" type="text" label={t('admin.tempPassword')}
            hint={t('auth.minLength', { n: String(min) })} required
            defaultValue={suggestPassword()} />
          <div>
            <Label>{t('admin.role')}</Label>
            <div className="mt-1">
              <Segmented size="sm" ariaLabel={t('admin.role')} value={role}
                onChange={(v) => setRole(v as Role)}
                options={[
                  { id: 'viewer', label: t('admin.role.viewer') },
                  { id: 'admin', label: t('admin.role.admin') },
                ]} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-[length:var(--text-data)] text-[var(--text-muted)]">
            <input type="checkbox" name="mustChange" defaultChecked className="size-4 accent-[var(--accent)]" />
            {t('admin.mustChange')}
          </label>
          {error && <Message tone="fail">{error}</Message>}
          <Button type="submit" variant="primary" className="w-full" disabled={create.isPending}>
            {t('admin.newUser')}
          </Button>
        </form>
      )}
    </Dialog>
  )
}

function ResetPassword({ user, onClose, min }: { user: AdminUser | null; onClose: () => void; min: number }) {
  const { t } = useT()
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const reset = useMutation({
    mutationFn: (v: { id: string; password: string }) =>
      api(`/api/admin/users/${v.id}/password`, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ password: v.password, mustChange: true }),
      }),
    onSuccess: (_r, v) => setDone(v.password),
    onError: (e) => setError((e as ApiError).message),
  })
  const close = () => { setDone(null); setError(null); onClose() }
  if (!user) return null

  return (
    <Dialog open onClose={close} title={`${t('admin.resetPassword')} — ${user.username}`}
      closeLabel={t('action.close')} width={440}>
      {done ? (
        <div className="space-y-2">
          <Message tone="pass">{t('admin.created1')}</Message>
          <Command>{done}</Command>
          <Button onClick={close} className="w-full">{t('action.close')}</Button>
        </div>
      ) : (
        <form className="space-y-3" onSubmit={(e) => {
          e.preventDefault(); setError(null)
          const f = new FormData(e.currentTarget)
          reset.mutate({ id: user.id, password: String(f.get('password') ?? '') })
        }}>
          <TextInput name="password" type="text" label={t('admin.tempPassword')}
            hint={t('auth.minLength', { n: String(min) })} defaultValue={suggestPassword()} required autoFocus />
          {error && <Message tone="fail">{error}</Message>}
          <Button type="submit" variant="primary" className="w-full" disabled={reset.isPending}>
            {t('admin.resetPassword')}
          </Button>
        </form>
      )}
    </Dialog>
  )
}

function Invites() {
  const { t, locale } = useT()
  const qc = useQueryClient()
  const { data } = useQuery({
    queryKey: ['admin', 'invites'],
    queryFn: () => api<{ invites: Invite[] }>('/api/admin/invites'),
  })
  const create = useMutation({
    mutationFn: () => api('/api/admin/invites', {
      method: 'POST', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ role: 'viewer', days: 7 }),
    }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['admin', 'invites'] }),
  })
  const revoke = useMutation({
    mutationFn: (token: string) => api(`/api/admin/invites/${token}`, { method: 'DELETE' }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['admin', 'invites'] }),
  })

  return (
    <section className="mt-5">
      <div className="mb-2 flex items-center gap-2">
        <Label>{t('admin.invites')}</Label>
        <div className="flex-1" />
        <Button size="sm" onClick={() => create.mutate()}>{t('admin.newInvite')}</Button>
      </div>
      {!data || data.invites.length === 0 ? (
        <p className="text-[length:var(--text-data)] text-[var(--text-faint)]">{t('admin.noInvites')}</p>
      ) : (
        <div className="space-y-1">
          {data.invites.map((i) => (
            <div key={i.token} className={cn(
              'flex flex-wrap items-center gap-2 rounded-[var(--radius-container)]',
              'border border-[var(--border)] bg-[var(--bg-raised)] px-3 py-2',
              i.usedAt && 'opacity-60',
            )}>
              <Command>{`${location.origin}/?invite=${i.token}`}</Command>
              <span className="text-[length:var(--text-micro)] text-[var(--text-faint)]">
                {i.usedAt ? t('admin.inviteUsed') : `${t('admin.inviteExpires')} ${timestamp(new Date(i.expiresAt).toISOString(), locale)}`}
              </span>
              <div className="flex-1" />
              {!i.usedAt && (
                <Button size="sm" variant="ghost" onClick={() => revoke.mutate(i.token)}>
                  {t('admin.revoke')}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

/** A suggestion, not a policy: the field stays editable. */
function suggestPassword(): string {
  const a = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  const r = crypto.getRandomValues(new Uint32Array(20))
  return Array.from(r, (x) => a[x % a.length]).join('')
}

function Column({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto w-full max-w-[84rem] px-4 pb-10 pt-5 sm:px-5">{children}</div>
}
